"use client";

import React, { useState, useRef } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-12 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="mx-auto size-12 rounded-full bg-green-100 flex items-center justify-center text-green-600">
          <ArrowRight className="size-6" />
        </div>
        <h3 className="text-xl font-serif text-foreground">{successMessage}</h3>
        <p className="text-muted-foreground">We'll be in touch shortly to schedule your consultation.</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8 mt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="border-0 border-b border-input rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-green-600 bg-transparent transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="border-0 border-b border-input rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-green-600 bg-transparent transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} className="border-0 border-b border-input rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-green-600 bg-transparent transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-sans">Company / Organization</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} className="border-0 border-b border-input rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-green-600 bg-transparent transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="challenge_summary" rules={{
      required: "Please describe your challenge"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-sans">Brief Description of Your Challenge</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Describe the biggest obstacle your business is facing..." rows={3} className="flex w-full border-0 border-b border-input bg-transparent px-0 py-2 text-sm shadow-none placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-green-600 disabled:cursor-not-allowed disabled:opacity-50 resize-none transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-sans">Preferred Consultation Date</FormLabel>
            <FormControl>
              <Input type="date" {...field} className="border-0 border-b border-input rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-green-600 bg-transparent transition-colors w-full" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md">
          {submitError}
        </div>}

      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={isSubmitting} className="bg-[var(--color-green-500)] text-white hover:bg-[var(--color-green-600)] rounded-sm px-8 h-12 text-base font-medium transition-all duration-300">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Submitting...
            </> : "Request Consultation"}
        </Button>
      </div>
    </Form>;
}
export default function GetStartedConsultation() {
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    setMousePosition({
      x: x * 0.2,
      y: y * 0.2
    });
  };
  const handleMouseLeave = () => {
    setMousePosition({
      x: 0,
      y: 0
    });
  };
  return <section id="get-started-consultation" className="relative w-full bg-background py-64 overflow-hidden">
      {}
      <Separator className="absolute top-0 left-0 w-full h-px bg-[var(--color-accent)] opacity-60" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="col-span-1 md:col-span-2" />
          
          <div className="col-span-1 md:col-span-8 flex flex-col items-center text-center space-y-12">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-foreground max-w-3xl">
              Ready to transform your business trajectory?
            </h2>

            <Dialog>
              <DialogTrigger asChild>
                <Button ref={buttonRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={{
                transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`
              }} className="bg-[var(--color-green-500)] text-white hover:bg-[var(--color-green-600)] rounded-[2px] px-10 py-7 text-lg font-medium transition-all duration-200 ease-out shadow-lg hover:shadow-xl">
                  Start Your Consultation
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl bg-background border-border p-0 overflow-hidden gap-0">
                <div className="p-8 md:p-10">
                  <DialogHeader className="mb-6 text-left">
                    <DialogTitle className="font-serif text-3xl text-foreground">
                      Get Started Consultation
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground text-base mt-2">
                      Tell us about your challenge. We'll prepare a tailored strategy session for you.
                    </DialogDescription>
                  </DialogHeader>

                  <FormProvider formId="get_started_consultation_form_services" sectionName="get-started-consultation" defaultValues={{
                  full_name: "",
                  email: "",
                  phone: "",
                  company_name: "",
                  challenge_summary: "",
                  preferred_date: ""
                }} validationRules={{
                  full_name: {
                    required: true
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  phone: {
                    required: true,
                    pattern: "phone"
                  },
                  challenge_summary: {
                    required: true
                  }
                }} submitText="Request Consultation" successMessage="Thank you! Your consultation request has been received.">
                    <ConsultationForm />
                  </FormProvider>
                </div>
                <div className="bg-muted/30 p-4 text-center text-xs text-muted-foreground border-t border-border">
                  Your information is secure and will never be shared with third parties.
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="col-span-1 md:col-span-2" />
        </div>
      </div>
    </section>;
}